import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Database, Calculator, Newspaper } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Site-wide "How our data is sourced" legal disclosure.
 *
 * Renders as a small underlined link (used in the footer + wherever we
 * want a compliance escape hatch); tapping it opens a modal that lays
 * out Propertic's three-bucket source hierarchy — required so that no
 * visitor can reasonably read a Propertic-calculated yield or a third-
 * party-derived rent as if DLD had published it directly.
 *
 * Content is bilingual and mirrors the same three buckets Roni's system
 * prompt enforces (ai_service.py: DLD raw / Propertic-calculated /
 * third-party market inputs).
 */
export default function DataSourceModal({ label }) {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const [open, setOpen] = useState(false);
  const openerRef = useRef(null);
  const panelRef = useRef(null);
  const linkLabel = label || (isAR ? "من أين تأتي أرقامنا" : "How our data is sourced");
  // R2-RTL-02: focus moves in, Tab stays inside, Escape closes, focus returns to the opener.
  useEffect(() => {
    if (!open) { return undefined; }
    const opener = openerRef.current;
    const t = setTimeout(() => panelRef.current?.querySelector("button, a[href]")?.focus(), 30);
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); setOpen(false); return; }
      if (e.key !== "Tab" || !panelRef.current) { return; }
      const f = [...panelRef.current.querySelectorAll("button, a[href], input, [tabindex]:not([tabindex='-1'])")];
      if (!f.length) { return; }
      const [first, last] = [f[0], f[f.length - 1]];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey, true); opener?.focus(); };
  }, [open]);
  return (
    <>
      <button
        type="button"
        ref={openerRef}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        data-testid="data-source-modal-open"
        className="hover:text-propertic-gold-2 transition-colors underline underline-offset-2 decoration-propertic-paper/30 hover:decoration-propertic-gold-2 text-start"
      >
        {linkLabel}
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              key="ds-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[95] bg-propertic-navy/70 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setOpen(false)}
              data-testid="data-source-modal"
              dir={isAR ? "rtl" : "ltr"}
            >
              <motion.div
                initial={{ y: 24, opacity: 0, scale: 0.98 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                exit={{ y: 12, opacity: 0, scale: 0.98 }}
                transition={{ type: "spring", stiffness: 260, damping: 26 }}
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={isAR ? "مصادر بياناتنا ومنهجيتها" : "How our data is sourced"}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl bg-propertic-paper rounded-[1.5rem] border border-propertic-gold/30 shadow-2xl overflow-hidden max-h-[92dvh] flex flex-col"
              >
                <button
                  onClick={() => setOpen(false)}
                  aria-label={isAR ? "إغلاق" : "Close"}
                  data-testid="data-source-modal-close"
                  className="absolute top-4 end-4 w-8 h-8 grid place-items-center rounded-full bg-propertic-sand/70 hover:bg-propertic-navy hover:text-propertic-paper text-propertic-muted z-10"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="p-6 sm:p-8 overflow-y-auto">
                  <p className="font-mono-brand text-propertic-gold text-[10px] tracking-[0.24em] uppercase mb-2">
                    {isAR ? "الشفافية أولاً" : "Transparency first"}
                  </p>
                  <h3 className="font-editorial text-2xl sm:text-3xl text-propertic-navy leading-tight mb-3">
                    {isAR ? "من أين تأتي أرقامنا" : "How our numbers are sourced"}
                  </h3>
                  <p className="text-sm text-propertic-ink-2 leading-relaxed mb-6">
                    {isAR
                      ? "كل رقم يظهر على هذا الموقع أو في محادثات روني يعود إلى أحد ثلاثة مصادر مصنّفة بوضوح. لا تُخلط الفئات، ولا تُنسب أرقام محسوبة إلى دائرة الأراضي، ولا تُعرض إيجارات من طرف ثالث كبيانات رسمية."
                      : "Every figure on this site, and in Roni's replies, comes from one of three clearly-labelled buckets. We never blend the categories, never attribute Propertic-calculated numbers to DLD, and never present third-party rental data as an official government publication."}
                  </p>

                  <div className="space-y-4">
                    <Bucket
                      icon={<Database className="w-4 h-4" />}
                      title={isAR ? "١) بيانات دائرة الأراضي والأملاك (خام)" : "1) DLD Open Data (raw)"}
                      body={isAR
                        ? "السجلات الأساسية للصفقات مأخوذة من بوابة البيانات المفتوحة لدائرة الأراضي والأملاك (dubailand.gov.ae · Dubai Pulse). وتحتسب بروبيرتك منها أعداد الصفقات وإجماليات القيم والحصص والقيم الوسيطة وفق المنهجية المعلنة؛ وهي مؤشرات محتسبة من سجلات الدائرة، وليست أرقاماً تُعاد نشرها حرفياً عن الدائرة. العزو المستخدَم: «دائرة الأراضي والأملاك · بوابة البيانات المفتوحة» للسجلات الأساسية."
                        : "Underlying transaction records come from the DLD Open Data Gateway (dubailand.gov.ae · Dubai Pulse). Propertic computes the counts, value totals, shares and medians from those records using the published methodology; they are Propertic-derived indicators, not figures republished verbatim by DLD. Raw records are attributed as \"Dubai Land Department · Open Data Gateway\"."}
                    />
                    <Bucket
                      icon={<Calculator className="w-4 h-4" />}
                      title={isAR ? "٢) حسابات Propertic (من بيانات دائرة الأراضي)" : "2) Propertic-calculated (from DLD raw)"}
                      body={isAR
                        ? "التغير السنوي (YoY)، والشهري (MoM)، والربعي (QoQ)، والعائد الإجمالي، وROI، وأي تجميع مخصّص عبر مناطق أو أشهر. تُذكر مع الصيغة الحسابية: «حساب Propertic باستخدام الصيغة: …». لا تُنسب هذه الأرقام إلى دائرة الأراضي مباشرة."
                        : "YoY / MoM / QoQ changes, gross rental yields, ROI, and any custom aggregation across areas or months. Cited with the underlying formula: \"Calculation by The Propertic using formula: …\". We never label these \"per DLD\"."}
                    />
                    <Bucket
                      icon={<Newspaper className="w-4 h-4" />}
                      title={isAR ? "٣) بيانات إيجار من مصادر خارجية" : "3) Third-party rental market inputs"}
                      body={isAR
                        ? "متوسط أسعار الطلب في إعلانات بوابات العقارات الإلكترونية (بيوت وبروبرتي فايندر وغيرها)، مُجمَّعة داخل بروبيرتك. لا تنشر دائرة الأراضي أسعار الطلب، وليست هذه أسعار عقود إيجاري. تُستخدم هذه المدخلات فقط ضمن احتساب العائد الإجمالي، ويُفصح روني عن المصدر بشكل صريح في أي إجابة تعتمد عليها."
                        : "Average asking rents from online property portal listings (Bayut, Property Finder and similar), curated inside The Propertic. DLD does not publish asking rents; these are not Ejari contract rents. They feed only into the gross-yield blend, and Roni discloses the specific portal source inline whenever an answer relies on it."}
                    />
                  </div>

                  <div className="mt-6 pt-5 border-t hairline">
                    <p className="text-[11px] text-propertic-muted leading-relaxed">
                      {isAR
                        ? "روني، مساعدنا الذكي، ملزَم داخلياً بالتمييز بين هذه الفئات في كل إجابة. أرقام دائرة الأراضي تُشير إلى \"دائرة الأراضي والأملاك\"، وحسابات Propertic تُذكر مع الصيغة، وأيّ مدخلات من طرف ثالث تُصرّح باسم مصدرها الفعلي داخل الرد."
                        : "Roni, our AI assistant, is prompt-locked to keep these buckets separate on every reply. DLD-raw figures cite \"Dubai Land Department\", Propertic calculations show the formula, and any third-party input names its actual source inline within the answer."}
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

function Bucket({ icon, title, body }) {
  return (
    <div className="rounded-xl border hairline bg-white p-4 flex gap-3 items-start">
      <span className="mt-0.5 w-8 h-8 shrink-0 rounded-full bg-propertic-sand grid place-items-center text-propertic-gold">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-editorial text-[15px] text-propertic-navy leading-tight mb-1">{title}</p>
        <p className="text-[12.5px] text-propertic-ink-2 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
